import { Badge, Button, Group, Modal, Select, Switch, Tabs, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useState } from 'react';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { EntityLabel } from '../components/EntityField';
import { describeSchedule } from '../components/ScheduleField';
import { useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumLabel, enumOptions } from '../lib/enums';
import { formatDateTime, formatRelative } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';
import { ComboModal } from './catalog/ComboModal';
import { ItemSetupModal } from './catalog/ItemSetupModal';
import { QuestionItemsModal } from './catalog/QuestionItemsModal';
import { RingsUpAt } from './catalog/RingsUpAt';
import { SaveTheMenu, snapshotKey } from './catalog/SaveTheMenu';

const ITEMS_PATH = '/admin/v1/catalog/items';
const ITEM_PAGE_PATH = '/admin/v1/catalog/item-page';
const CATEGORIES_PATH = '/admin/v1/catalog/categories';
const BUNDLES_PATH = '/admin/v1/catalog/bundles';
const GROUPS_PATH = '/admin/v1/catalog/modifier-groups';
const VERSIONS_PATH = '/admin/v1/catalog/versions';
const ITEM_PAGE_SIZE = 50;

interface CatalogItemPage {
  rows: Record<string, unknown>[];
  total: number;
}

/**
 * Whether an item can be ordered right now.
 *
 * Reads as availability rather than as the trade term the endpoint uses: the manager reaching for
 * this has just been told the kitchen is out of snapper, and "86" is a word half the staff know.
 * The switch is on when the item is orderable, so on/off matches the menu, not the flag.
 */
function AvailabilitySwitch({ row }: { row: Record<string, unknown> }) {
  const queryClient = useQueryClient();
  const name = String(row.nameEn ?? 'this item');

  const toggle = useMutation({
    mutationFn: (available: boolean) =>
      rawApi('POST', `${ITEMS_PATH}/${String(row.id)}/eighty-six`, { eightySixed: !available }),
    onSuccess: (_result, available) => {
      void queryClient.invalidateQueries({ queryKey: listKey(ITEMS_PATH) });
      void queryClient.invalidateQueries({ queryKey: listKey(ITEM_PAGE_PATH) });
      notifyDone(
        available ? 'Back on the menu' : 'Off the menu',
        available
          ? `${name} can be ordered again.`
          : `${name} cannot be ordered until you switch it back on.`,
      );
    },
    onError: (err) => notifyError(err, `change whether ${name} can be ordered`),
  });

  const available = !row.eightySixed;
  return (
    <Switch
      size="sm"
      checked={available}
      disabled={toggle.isPending}
      onChange={(e) => toggle.mutate(e.currentTarget.checked)}
      label={available ? 'Can order' : 'Ran out'}
    />
  );
}

/**
 * How much of the menu a category actually holds.
 *
 * A category with nothing in it is a tab staff tap on the register to find an empty screen, and it
 * looked exactly like a working one here. This is also the answer you want before hiding one —
 * "Cocktails" carrying thirty drinks is not something to switch off on a hunch.
 *
 * Shares the item list the Items tab has already loaded rather than asking for it again.
 */
function CategoryItemCount({ categoryId }: { categoryId: unknown }) {
  const items = useQuery({
    queryKey: listKey(ITEMS_PATH),
    queryFn: () => rawApi<{ categoryId?: string }[]>('GET', ITEMS_PATH),
  });
  if (!items.data) return null;
  const count = items.data.filter((i) => i.categoryId === categoryId).length;
  if (count === 0) {
    return (
      <Badge color="orange" variant="light">
        Nothing in it
      </Badge>
    );
  }
  return (
    <Text size="sm">
      {count} {count === 1 ? 'item' : 'items'}
    </Text>
  );
}

/** What a serving window applies to changes row by row, so no single column reference fits. */
function ScheduleTarget({ row }: { row: Record<string, unknown> }) {
  const kind = enumLabel('availabilityTargetType', row.targetType);
  const entity =
    row.targetType === 'menuItem'
      ? ('menuItem' as const)
      : row.targetType === 'variant'
        ? ('variant' as const)
        : row.targetType === 'category'
          ? ('category' as const)
          : row.targetType === 'bundle'
            ? ('bundle' as const)
            : null;
  // Only reached if a rule was written against something this build does not know how to name —
  // the kind on its own still says why there is no name, which beats a dash.
  if (!entity) return <Text c="dimmed">{kind}</Text>;
  return (
    <div>
      <EntityLabel entity={entity} id={row.targetId} />
      <Text size="xs" c="dimmed">
        {kind}
      </Text>
    </div>
  );
}

function YesNo({ on, yes, no }: { on: unknown; yes: string; no: string }) {
  return (
    <Badge color={on ? 'teal' : 'gray'} variant="light">
      {on ? yes : no}
    </Badge>
  );
}

/**
 * How many answers staff may pick, in a sentence.
 *
 * Three columns said this before — "Staff must answer", "Fewest they can pick", "Most they can
 * pick" — and between them they could show `Optional / 0 / —`, which is the storage talking. It
 * takes a manager three glances and a subtraction to learn what a register will do, and the two
 * combinations that are contradictory (compulsory, but nothing has to be picked) look no different
 * from the ones that work.
 */
function describePicks(row: Record<string, unknown>): string {
  const min = Number(row.minSelections ?? 0);
  const max = row.maxSelections == null ? null : Number(row.maxSelections);
  const floor = row.required ? Math.max(1, min) : min;
  // A ceiling under the floor is not a rule any register can obey, and this form takes both numbers as
  // free text, so it is one slip away at all times. Said out loud, because the sentence underneath
  // would otherwise describe one of the two numbers and hide the other: "most they can pick: 1"
  // beside "fewest: 2" used to render as "One, or none", which is a working rule that is not there.
  if (max !== null && max < floor) return `Needs ${floor} but allows ${max} — staff cannot ring it`;
  if (max === 0) return 'Nothing can be picked';
  if (max === 1) return floor === 1 ? 'Exactly one' : 'One, or none';
  if (max === null) return floor === 0 ? 'Any number' : `${floor} or more`;
  if (floor === 0) return `Up to ${max}`;
  if (floor === max) return `Exactly ${floor}`;
  return `${floor} to ${max}`;
}

/**
 * The answers inside one question.
 *
 * A set of choices with nothing in it is not a half-finished record, it is a question the register
 * cannot ask — so the count sits in the table and the answers are edited right here rather than
 * being a thing you have to know exists.
 */
function ChoicesButton({ group }: { group: Record<string, unknown> }) {
  const [opened, setOpened] = useState(false);
  const groupId = String(group.id);
  const question = String(group.nameEn ?? 'these choices');
  const optionsPath = `${GROUPS_PATH}/${groupId}/options`;

  return (
    <>
      <Button size="xs" variant="light" onClick={() => setOpened(true)}>
        Answers
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={question}
        size="xl"
        closeOnClickOutside={false}
      >
        <CrudSection
          title="Answers staff can pick"
          entityNoun="answer"
          description="What appears on the register when this question comes up. Send the menu to the registers once you have finished changing them."
          listPath={optionsPath}
          createPath="/admin/v1/catalog/modifier-options"
          updatePathTemplate="/admin/v1/catalog/modifier-options/{id}"
          defaults={{ modifierGroupId: groupId }}
          rowLabel={(r) => String(r.nameEn ?? 'this answer')}
          emptyMessage="No answers yet. Until you add some, the register has nothing to offer for this question."
          columns={[
            { accessor: 'nameEn', title: 'Answer' },
            {
              accessor: 'priceDeltaMinor',
              title: 'Changes the price by',
              unit: 'money',
              currencyAccessor: 'priceDeltaCurrency',
            },
            {
              accessor: 'isDefault',
              title: 'Picked already',
              render: (r) => <YesNo on={r.isDefault} yes="Picked" no="Not picked" />,
            },
            {
              accessor: 'active',
              title: 'On the register',
              render: (r) => <YesNo on={r.active} yes="Shown" no="Hidden" />,
            },
          ]}
          fields={[
            {
              name: 'nameEn',
              label: 'Answer',
              type: 'text',
              required: true,
              description: 'What staff read on the register, such as "Medium rare"',
            },
            { name: 'nameEs', label: 'Answer in Spanish', type: 'text', nullable: true },
            {
              name: 'priceDeltaMinor',
              label: 'Changes the price by',
              type: 'number',
              unit: 'money',
              defaultValue: 0,
              description: 'Leave at zero when it costs nothing. Enter a negative amount to take money off.',
            },
            {
              name: 'priceDeltaCurrency',
              label: 'In which money',
              type: 'select',
              options: enumOptions('currency'),
              defaultValue: 'USD',
            },
            {
              name: 'isDefault',
              label: 'Already picked when the question comes up',
              type: 'boolean',
              description: 'For the usual answer, so staff only tap when the guest wants something else',
            },
            {
              name: 'displayOrder',
              label: 'Position in the list',
              type: 'number',
              defaultValue: 0,
              description: 'Lower numbers come first',
            },
            {
              name: 'active',
              label: 'Show this answer on the register',
              type: 'boolean',
              defaultValue: true,
            },
          ]}
        />
      </Modal>
    </>
  );
}

/**
 * When the registers last got the menu.
 *
 * Everything on this screen is edited in the office and reaches a register only when it is sent. That
 * made a silent failure possible and easy: a price corrected on Monday and never sent is a price
 * staff are still ringing up wrong on Friday, and nothing anywhere said so. The date is the cheap,
 * honest version — it cannot claim there is nothing outstanding, only say when the last send was.
 */
function LastSent({ locationId }: { locationId: string | undefined }) {
  const versions = useQuery({
    queryKey: listKey(VERSIONS_PATH, { locationId }),
    queryFn: () => rawApi<{ publishedAt: string }[]>('GET', `${VERSIONS_PATH}?locationId=${locationId}`),
    enabled: Boolean(locationId),
  });

  const last = versions.data?.[0]?.publishedAt;
  return (
    <Text size="sm" c="dimmed">
      {versions.isLoading
        ? ' '
        : last
          ? `Registers last got the menu ${formatDateTime(last)} · ${formatRelative(last)}`
          : 'The registers have never been sent this menu.'}
    </Text>
  );
}

/** The tabs, in the order they are shown, so an address can only name one that exists. */
const TABS = ['items', 'categories', 'modifiers', 'bundles', 'availability'] as const;

export function CatalogPage() {
  const locationId = useLocationId();
  // In the address for the same reason as every other tabbed page: a refresh in the middle of
  // building a combo used to land back on Items, and nothing could link to a tab but the first.
  const [tab, setTab] = useTabInUrl(TABS, 'items');
  const queryClient = useQueryClient();
  const [setupItem, setSetupItem] = useState<Record<string, unknown> | null>(null);
  const [questionItems, setQuestionItems] = useState<Record<string, unknown> | null>(null);
  const [comboId, setComboId] = useState<string | null>(null);
  const [addingCombo, setAddingCombo] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [itemAsked] = useDebouncedValue(itemSearch.trim(), 250);
  const [itemCategory, setItemCategory] = useState<string | null>(null);
  const [itemVisibility, setItemVisibility] = useState<string | null>(null);
  const [itemAvailability, setItemAvailability] = useState<string | null>(null);
  const [itemStation, setItemStation] = useState<string | null>(null);
  const [itemPage, setItemPage] = useState(1);
  const [itemSort, setItemSort] = useState<DataTableSortStatus<Record<string, unknown>>>({
    columnAccessor: 'nameEn',
    direction: 'asc',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: every server-side filter starts from page one
  useEffect(
    () => setItemPage(1),
    [itemAsked, itemCategory, itemVisibility, itemAvailability, itemStation, locationId],
  );

  const categoryOptions = useQuery({
    queryKey: listKey(CATEGORIES_PATH),
    queryFn: () => rawApi<{ id: string; nameEn: string }[]>('GET', CATEGORIES_PATH),
  });
  const itemListQuery = {
    locationId,
    q: itemAsked || undefined,
    categoryId: itemCategory || undefined,
    visibility: itemVisibility || undefined,
    availability: itemAvailability || undefined,
    kdsStation: itemStation || undefined,
    sort: itemSort.columnAccessor,
    direction: itemSort.direction,
    page: itemPage,
  };

  const publish = useMutation({
    mutationFn: () => rawApi('POST', '/admin/v1/catalog/publish', { locationId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(VERSIONS_PATH, { locationId }) });
      // Publishing is what CREATES the frozen document the panel below reads, so the panel has to
      // be thrown away too. Without this it went on reading "nothing has been sent to the registers
      // yet" underneath a green "Menu sent", which is the failure mode api/queryKeys.ts was
      // written about: it teaches people to distrust a system that is working.
      void queryClient.invalidateQueries({ queryKey: snapshotKey(locationId) });
      notifyDone('Menu sent', 'Every register and kitchen screen is now working from this menu.');
    },
    onError: (err) => notifyError(err, 'send the menu to the registers'),
  });

  // Publishing reaches every device at once, so it asks first and says plainly what will happen.
  const confirmPublish = () => {
    if (!locationId) return;
    modals.openConfirmModal({
      title: 'Send this menu to the registers?',
      children: (
        <Text size="sm">
          Every register and kitchen screen will switch to the menu exactly as it stands now — items, sizes,
          categories, choices, combos, and anything marked as run out. Orders already open are not affected.
          You can send it again any time you make a change.
        </Text>
      ),
      labels: { confirm: 'Send it', cancel: 'Not yet' },
      onConfirm: () => publish.mutate(),
    });
  };

  return (
    <>
      {/* The send button sits above the tabs because every tab changes something that only
          reaches a register when the menu is sent — it used to live on the Items tab alone, so
          editing a combo and then looking for it was a hunt through screens. */}
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <div>
          <Title order={3}>Menu</Title>
          <LastSent locationId={locationId} />
        </div>
        <Button loading={publish.isPending} onClick={confirmPublish} disabled={!locationId}>
          Send menu to registers
        </Button>
      </Group>

      {/* Beside the send button rather than at the foot of the page, because it is the same
          gesture from the other side: sending freezes a copy of the whole menu, and this is that
          copy. It also answers "why is that price still wrong on the register", which is a question
          asked from behind the bar and not from the bottom of a settings screen. */}
      <SaveTheMenu />

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="items">Items</Tabs.Tab>
          <Tabs.Tab value="categories">Categories</Tabs.Tab>
          <Tabs.Tab value="modifiers">Choices and extras</Tabs.Tab>
          <Tabs.Tab value="bundles">Combos</Tabs.Tab>
          <Tabs.Tab value="availability">Serving times</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="items">
          <CrudSection
            title="Items"
            entityNoun="menu item"
            description="Everything you sell, including retired dishes kept for later. Open one to price its sizes, set the questions the register asks about it, and say what it contains."
            listPath={ITEM_PAGE_PATH}
            listEnabled={Boolean(locationId)}
            listQuery={itemListQuery}
            createPath={ITEMS_PATH}
            updatePathTemplate={`${ITEMS_PATH}/{id}`}
            defaults={{ locationId }}
            extractList={(data) => (data as CatalogItemPage).rows ?? []}
            pagination={{
              page: itemPage,
              recordsPerPage: ITEM_PAGE_SIZE,
              totalRecords: (data) => (data as CatalogItemPage | undefined)?.total ?? 0,
              onPageChange: setItemPage,
            }}
            sortStatus={itemSort}
            onSortStatusChange={(status) => {
              setItemSort(status);
              setItemPage(1);
            }}
            tableControls={
              <Group align="flex-end" wrap="wrap">
                <TextInput
                  label="Search"
                  placeholder="Item name"
                  value={itemSearch}
                  onChange={(event) => setItemSearch(event.currentTarget.value)}
                  w={240}
                />
                <Select
                  label="Category"
                  placeholder="All categories"
                  clearable
                  searchable
                  value={itemCategory}
                  onChange={setItemCategory}
                  data={(categoryOptions.data ?? []).map((category) => ({
                    value: category.id,
                    label: category.nameEn,
                  }))}
                  w={200}
                />
                <Select
                  label="On the register"
                  placeholder="Shown and retired"
                  clearable
                  value={itemVisibility}
                  onChange={setItemVisibility}
                  data={[
                    { value: 'shown', label: 'Shown' },
                    { value: 'hidden', label: 'Retired (Admin only)' },
                  ]}
                  w={190}
                />
                <Select
                  label="Availability"
                  placeholder="Any availability"
                  clearable
                  value={itemAvailability}
                  onChange={setItemAvailability}
                  data={[
                    { value: 'available', label: 'Available' },
                    { value: 'soldOut', label: 'Sold out' },
                  ]}
                  w={175}
                />
                <Select
                  label="Prepared at"
                  placeholder="Every station"
                  clearable
                  value={itemStation}
                  onChange={setItemStation}
                  data={enumOptions('prepStation')}
                  w={175}
                />
              </Group>
            }
            rowLabel={(r) => String(r.nameEn ?? 'this item')}
            // A new item has a size and no price, which means staff cannot ring it up — the job is
            // not done when the form closes. It used to close onto a table where the only sign was
            // absence, so the item sat there looking finished. Adding one now lands in the item
            // itself, with its size and the price box in front of you.
            onCreated={(created) => setSetupItem(created)}
            emptyMessage="Nothing on the menu yet. Add your first item — you will need a category and a tax rate set up first."
            rowActions={(row) => (
              <Button size="xs" variant="light" onClick={() => setSetupItem(row)}>
                Sizes and choices
              </Button>
            )}
            columns={[
              { accessor: 'nameEn', title: 'Item', sortable: true },
              { accessor: 'categoryName', title: 'Category', sortable: true },
              { accessor: 'kdsStation', title: 'Prepared at', enumGroup: 'prepStation', sortable: true },
              // Course is set once when an item is added and never scanned down a list — on a new
              // menu it was a full column reading "No course" on every row. It stays on the form,
              // where it is decided.
              {
                accessor: 'hidden',
                title: 'On the register',
                sortable: true,
                render: (r) => <YesNo on={!r.hidden} yes="Shown" no="Retired" />,
              },
              {
                accessor: 'eightySixed',
                title: 'Availability',
                noWrap: true,
                sortable: true,
                render: (r) => <AvailabilitySwitch row={r} />,
              },
            ]}
            fields={[
              { name: 'nameEn', label: 'Name', type: 'text', required: true },
              {
                name: 'nameEs',
                label: 'Name in Spanish',
                type: 'text',
                nullable: true,
                description: 'Used on registers and tickets set to Spanish',
              },
              { name: 'categoryId', label: 'Category', type: 'entity', resource: 'category', required: true },
              {
                name: 'taxGroupId',
                label: 'Sales tax',
                type: 'entity',
                resource: 'taxGroup',
                required: true,
                description: 'Which tax this item is sold under. Set these up on the Tax screen.',
              },
              {
                name: 'kdsStation',
                label: 'Prepared at',
                type: 'select',
                options: enumOptions('prepStation'),
                description: 'Where the ticket prints when this is ordered',
              },
              {
                name: 'course',
                label: 'Course',
                type: 'select',
                options: enumOptions('course'),
                description: 'Used to group a table order so starters land before mains',
              },
              {
                name: 'prepTimeMinutes',
                label: 'Typical prep time in minutes',
                type: 'number',
                nullable: true,
                description: 'Roughly how long the kitchen needs. Leave blank if it goes out straight away.',
              },
              {
                /*
                 * COMES FROM THE TAX SET NOW, and this box is the override.
                 *
                 * The comment that used to sit here argued the flag must be set per item because
                 * the alcohol tax set also covers tobacco and because a virgin cocktail is in
                 * Cocktails. The second half was an argument about the CATEGORY: a virgin mojito
                 * belongs in the standard tax set, at 15%, which is what the tax office says about
                 * it — so the tax set gets that case right and the category is what gets it wrong.
                 * The first half is real, which is why the box is still here.
                 *
                 * What it cost was the default. A new cocktail was put in the 18% set and left
                 * unticked, so a plan that leaves alcohol out paid a driver on it and nothing ever
                 * said the two disagreed. The help text below used to say "already ticked", which
                 * was true of a one-off backfill and of nothing anybody added afterwards.
                 */
                name: 'isAlcohol',
                label: 'This is alcohol',
                type: 'boolean',
                description:
                  'Comes from the tax set you choose, so a drink in the alcohol set is already alcohol. Change it here for something taxed as alcohol that nobody drinks — a pack of cigarettes — or for a virgin cocktail, so drivers still earn on it.',
              },
              {
                name: 'hidden',
                label: 'Hide from the register',
                type: 'boolean',
                description: 'Keeps the item and its history but stops staff seeing it — for seasonal dishes',
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="categories">
          <CrudSection
            title="Categories"
            entityNoun="category"
            description="The groups items sit in on the register — Beers, Starters, Cocktails. The order you set here is the order staff see."
            listPath="/admin/v1/catalog/categories"
            updatePathTemplate="/admin/v1/catalog/categories/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.nameEn ?? 'this category')}
            emptyMessage="No categories yet. Add one — Beers or Starters — before you add items."
            columns={[
              { accessor: 'nameEn', title: 'Category' },
              { accessor: 'nameEs', title: 'In Spanish' },
              // Which part of the register holds this group. It cannot be worked out from anything
              // else on the row — the name says "Snacks" and says nothing about whether staff will
              // find it under Food or under Drinks — so it is a column rather than a detail you
              // have to open the group to read.
              { accessor: 'kind', title: 'Found under', enumGroup: 'categoryKind' },
              // No "Position" column: these rows are already listed in the order staff see, so the
              // number was the sort key showing through. What it cost was the column below, which
              // answers a question you cannot work out by looking.
              {
                accessor: 'id',
                title: 'On the menu',
                render: (r) => <CategoryItemCount categoryId={r.id} />,
              },
              {
                accessor: 'active',
                title: 'On the register',
                render: (r) => <YesNo on={r.active} yes="Shown" no="Hidden" />,
              },
            ]}
            fields={[
              { name: 'nameEn', label: 'Name', type: 'text', required: true },
              {
                name: 'nameEs',
                label: 'Name in Spanish',
                type: 'text',
                nullable: true,
                description: 'Used on registers set to Spanish',
              },
              {
                name: 'kind',
                label: 'Found under',
                type: 'select',
                enumGroup: 'categoryKind',
                required: true,
                // Food, the same answer the API gives a group nobody has filed: it works the
                // choice out from what is already in the group, and a group being added holds
                // nothing. Seeded here for the reason `openCreate` gives about every default —
                // a form that starts on a different answer from the one the server would pick
                // saves a group into a part of the register the owner never chose.
                defaultValue: 'food',
                description:
                  'Where staff will look for this group on the register — not where its items are made.',
              },
              {
                name: 'displayOrder',
                label: 'Position on the register',
                type: 'number',
                // It used to promise that categories left at 0 sort alphabetically. They do not —
                // they come back in no particular order — and a manager who trusted that would
                // wonder why Beers keeps moving around on the register.
                description: 'Lower numbers come first. Give the ones you want at the front a number.',
              },
              {
                name: 'active',
                label: 'Show this category on the register',
                type: 'boolean',
                defaultValue: true,
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="modifiers">
          <CrudSection
            title="Choices and extras"
            entityNoun="set of choices"
            description="What the register asks staff — how it is cooked, which rum, extra chips. A question can belong directly to an item or appear only after one answer, such as Fried or Grilled after Plantains."
            listPath={GROUPS_PATH}
            updatePathTemplate={`${GROUPS_PATH}/{id}`}
            defaults={{ locationId }}
            rowLabel={(r) => String(r.nameEn ?? 'these choices')}
            emptyMessage="No choices set up yet. Add one — Cooked how? or Pick a side."
            rowActions={(row) => (
              <>
                {/* The "No items" badge beside this said the question reaches no guest, and the
                    only cure was to open each dish in turn and pick this question out of a list
                    of every question on the menu. The row that raises it now settles it. */}
                <Button size="xs" variant="light" onClick={() => setQuestionItems(row)}>
                  Where it is asked
                </Button>
                <ChoicesButton group={row} />
              </>
            )}
            columns={[
              { accessor: 'nameEn', title: 'Question' },
              // The two ways a question quietly does nothing. Both were invisible: a question with
              // no answers and a question on no item looked exactly like a working one, and the
              // first sign of either was a plate leaving the kitchen wrong.
              {
                accessor: 'optionCount',
                title: 'Answers',
                render: (r) =>
                  Number(r.optionCount ?? 0) === 0 ? (
                    <Badge color="orange" variant="light">
                      None yet
                    </Badge>
                  ) : (
                    <Text size="sm">{String(r.optionCount)}</Text>
                  ),
              },
              {
                accessor: 'itemCount',
                title: 'Asked on',
                render: (r) => {
                  const items = Number(r.itemCount ?? 0);
                  const conditions = Number(r.conditionalCount ?? 0);
                  return items + conditions === 0 ? (
                    <Badge color="orange" variant="light">
                      Nowhere
                    </Badge>
                  ) : (
                    <Text size="sm">
                      {items > 0 && `${items} ${items === 1 ? 'item' : 'items'}`}
                      {items > 0 && conditions > 0 && ', '}
                      {conditions > 0 && `${conditions} follow-up${conditions === 1 ? '' : 's'}`}
                    </Text>
                  );
                },
              },
              { accessor: 'maxSelections', title: 'Staff can pick', render: (r) => describePicks(r) },
            ]}
            fields={[
              {
                name: 'nameEn',
                label: 'Question',
                type: 'text',
                required: true,
                description: 'What staff read on the register, such as "Cooked how?"',
              },
              { name: 'nameEs', label: 'Question in Spanish', type: 'text', nullable: true },
              {
                name: 'required',
                label: 'Staff must answer before the item can be rung',
                type: 'boolean',
              },
              {
                name: 'minSelections',
                label: 'Fewest they can pick',
                type: 'number',
                description: 'Leave at 0 unless a guest has to choose more than one',
              },
              {
                name: 'maxSelections',
                label: 'Most they can pick',
                type: 'number',
                nullable: true,
                description:
                  'Set 1 for a question with one answer, such as how a steak is cooked. Leave blank for no limit.',
                // Caught here rather than described later: a ceiling under the floor is a question
                // no register can satisfy, so staff reach the item and cannot ring it at all — on a
                // busy night, at the register, with the guest waiting.
                validate: (value, values) => {
                  if (value === '' || value === null || value === undefined) return null;
                  const most = Number(value);
                  if (most < 1) return 'Set at least 1, or leave this blank for no limit';
                  const typed = Number(values.minSelections ?? 0);
                  const fewest = values.required ? Math.max(1, typed) : typed;
                  return most < fewest
                    ? `Staff would have to pick at least ${fewest} and no more than ${most}, which cannot be done`
                    : null;
                },
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="bundles">
          <CrudSection
            title="Combos"
            description="Beer buckets, daily specials and set meals that ring up as one item. Open one to see what is inside it, change the picks, or stop selling it."
            listPath={BUNDLES_PATH}
            emptyMessage="No combos set up yet."
            actions={
              <Button size="sm" onClick={() => setAddingCombo(true)} disabled={!locationId}>
                Add a combo
              </Button>
            }
            rowActions={(row) => (
              <Button size="xs" variant="light" onClick={() => setComboId(String(row.id))}>
                What is in it
              </Button>
            )}
            columns={[
              { accessor: 'nameEn', title: 'Combo' },
              // A combo is priced as a whole, and an unpriced one looked exactly like a working
              // one here — right up to the moment a server could not ring up the beer bucket
              // somebody built on Friday. Tapping the cell prices it, on this row, because the
              // row is the combo the price is for.
              {
                accessor: 'id',
                title: 'Rings up at',
                render: (r) => (
                  <RingsUpAt
                    targetType="bundle"
                    targetId={String(r.id)}
                    name={String(r.nameEn ?? 'this combo')}
                  />
                ),
              },
              { accessor: 'descriptionEn', title: 'Description' },
              {
                accessor: 'active',
                title: 'On the register',
                render: (r) => <YesNo on={r.active} yes="Selling" no="Off" />,
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="availability">
          <CrudSection
            title="Serving times"
            entityNoun="serving rule"
            description="Limit when something can be ordered — breakfast until 10, a Wednesday special, or fried food off while the fryer is down. Lift a rule by editing it and turning it off; the hours stay for next time."
            listPath="/admin/v1/catalog/availability-schedules"
            updatePathTemplate="/admin/v1/catalog/availability-schedules/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.note ?? 'this rule')}
            emptyMessage="No serving times set. Everything on the menu can be ordered whenever you are open."
            columns={[
              // One column, not two: the type of thing and the thing itself were separate columns
              // saying "One item" beside "Snapper", which is half a sentence spread over two.
              { accessor: 'targetId', title: 'Applies to', render: (r) => <ScheduleTarget row={r} /> },
              { accessor: 'effect', title: 'Rule', enumGroup: 'scheduleKind' },
              // The hours were not on the table at all, which is the one thing a serving rule is
              // for: "breakfast until 10:30" was stored, and the screen said only that a rule existed.
              { accessor: 'schedule', title: 'When', render: (r) => describeSchedule(r.schedule) },
              { accessor: 'note', title: 'Note' },
              {
                accessor: 'active',
                title: 'In force',
                render: (r) => <YesNo on={r.active} yes="In force" no="Lifted" />,
              },
            ]}
            fields={[
              {
                name: 'targetType',
                label: 'Applies to',
                type: 'select',
                required: true,
                options: enumOptions('availabilityTargetType'),
              },
              {
                name: 'targetId',
                label: 'Which one',
                type: 'entity',
                required: true,
                // Sizes are offered here because happy hour is usually about one of them — the
                // pitcher, not the bottle — and every size across the menu is now listable by the
                // drink it belongs to, so the picker has something to show.
                resource: (values) => {
                  if (values.targetType === 'category') return 'category';
                  if (values.targetType === 'menuItem') return 'menuItem';
                  if (values.targetType === 'variant') return 'variant';
                  if (values.targetType === 'bundle') return 'bundle';
                  return undefined;
                },
              },
              {
                name: 'effect',
                label: 'Rule',
                type: 'select',
                required: true,
                options: enumOptions('scheduleKind'),
                description:
                  'Whether the hours below are the only time it is served, or the one time it is not',
              },
              {
                name: 'schedule',
                label: 'Hours',
                type: 'schedule',
                required: true,
                placeholder: 'No hours set yet — this rule does nothing until you add some.',
                description: 'Pick the days and the times. Breakfast until 10:30, Monday to Friday.',
              },
              {
                name: 'channelId',
                label: 'Only for one ordering channel',
                type: 'entity',
                resource: 'channel',
                nullable: true,
                description: 'Leave blank to apply everywhere, or pick one to limit it to a delivery app',
              },
              {
                name: 'note',
                label: 'Note',
                type: 'text',
                nullable: true,
                description: 'Why this rule exists, so whoever finds it later knows',
              },
              {
                name: 'active',
                label: 'This rule is in force',
                type: 'boolean',
                defaultValue: true,
                description: 'Turn it off when the fryer is mended, rather than deleting the hours',
              },
            ]}
          />
        </Tabs.Panel>
      </Tabs>

      <ItemSetupModal item={setupItem} onClose={() => setSetupItem(null)} />

      <QuestionItemsModal group={questionItems} onClose={() => setQuestionItems(null)} />

      <ComboModal
        opened={Boolean(comboId) || addingCombo}
        bundleId={comboId}
        locationId={locationId}
        onClose={() => {
          setComboId(null);
          setAddingCombo(false);
        }}
      />
    </>
  );
}
